import React, { ChangeEvent, DragEvent, useContext, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { useMutation } from '@apollo/react-hooks'
import _get from 'lodash/get'

import { makeStyles } from '@material-ui/core/styles'
import Paper from '@material-ui/core/Paper'
import Icon from '@material-ui/core/Icon'
import Button from '@material-ui/core/Button'
import CircularProgress from '@material-ui/core/CircularProgress'
import IconButton from '@material-ui/core/IconButton'
import LinearProgress from '@material-ui/core/LinearProgress'
import Typography from '@material-ui/core/Typography'
import CloseIcon from '@material-ui/icons/Close'

import { useAuth0 } from '@/auth/index'
import useMounted from '@/components/hooks/use-mounted'
import { errorHandler } from '@/shared/error-handler'
import { Context } from '@/shared/store/reducers/global-reducer'
import { UPDATE_PROJECT } from '@/shared/graphql/mutations/mutationProjects'
import { callPagApi } from '@/shared/services/pag-api'
import { uploadProjectThumbnail } from '@/shared/services/prodigy-file'
import { migratedImage } from '@/utils/migrated-image'

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'relative',
    width: '100%',
    '&:hover': {
      backgroundColor: theme.palette.grey[200],
      cursor: 'pointer',
      '& .image-over-layer': {
        display: 'inherit',
      },
    },
    '& .image-over-layer': {
      display: 'none',
      position: 'absolute',
      width: '100%',
      height: '100%',
      left: 0,
      top: 0,
      backgroundColor: theme.palette.grey[200],
      opacity: '0.4',
    },
  },
  projectImage: {
    width: 164,
    height: 114,
    objectFit: 'contain',
    objectPosition: 'top'
  },

  projectImageEdit: {
    position: 'absolute',
    marginTop: 12,
    textAlign: 'center',
    width: 316,
    zIndex: 1001,
  },
  cardContent: {
    position: 'relative',
    padding: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  projectImageWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.grey[200],
    height: 198,
  },
  projectImageEditImage: {
    width: 284,
    height: 198,
  },
  backdrop: {
    position: 'fixed',
    inset: 0,
    zIndex: 1300,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(3),
    background: 'rgba(38, 43, 51, 0.48)',
  },
  modalRoot: {
    width: 420,
    maxWidth: '100%',
    padding: theme.spacing(2),
    borderRadius: 16,
    background: '#f5f8fc',
    border: '1px solid rgba(34, 80, 153, 0.2)',
    boxShadow: '0 24px 56px rgba(20, 35, 64, 0.32)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  title: {
    color: '#225099',
    fontWeight: 700,
  },
  headerControls: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  closeButton: {
    padding: 4,
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  dropZone: {
    display: 'flex',
    minHeight: 132,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(2.5),
    borderRadius: 14,
    border: '2px dashed rgba(34, 80, 153, 0.32)',
    background: '#ffffff',
    color: theme.palette.text.secondary,
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'border-color 160ms ease, background-color 160ms ease, transform 160ms ease',
    '&:hover': {
      borderColor: '#225099',
      background: '#eef4fb',
    },
  },
  dropZoneActive: {
    borderColor: '#225099',
    background: '#e7f0fb',
    transform: 'translateY(-1px)',
  },
  iconBubble: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 48,
    height: 48,
    borderRadius: '50%',
    background: '#225099',
    color: '#ffffff',
    fontSize: '1.5rem',
    fontWeight: 700,
  },
  progressWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
  },
  statusSuccess: {
    color: '#17663a',
    fontWeight: 600,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
  },
  input: {
    display: 'none',
  },
  error: {
    color: theme.palette.error.main,
  },
}))

export const ProjectImageEdit = (props: any) => {
  const { projectId, shouldUpdateProject = true, handleCancel, handleChangeProjectImage } = props
  const { getTokenSilently, getIdTokenClaims } = useAuth0()
  const { store } = useContext(Context)
  const { activeTenant } = store
  const mountedRef = useMounted(true)
  const uploadingRef = useRef(false)
  const uploadingImageRef = useRef<string | null>(null)

  const inputRef = useRef<HTMLInputElement | null>(null)
  const uploadControllerRef = useRef<AbortController | null>(null)

  const [progress, setProgress] = useState(0)
  const [status, setStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle')
  const [errorMsg, setErrorMsg] = useState('')
  const [isDragging, setIsDragging] = useState(false)

  const [updateProject, updateProjectRes] = useMutation(UPDATE_PROJECT, {
    onError: (err) => {
      errorHandler(err)
      if (!mountedRef.current) return
    },
    onCompleted: (res) => {
      if (!mountedRef.current) return
      if (res && uploadingImageRef.current) handleChangeProjectImage(uploadingImageRef.current)
    },
  })

  useEffect(() => {
    return () => {
      mountedRef.current = false
      if (uploadControllerRef.current) {
        uploadControllerRef.current.abort()
      }
    }
  }, [mountedRef])

  const handleChangeFile = (url: string) => {
    let abortController: any
    try {
      if (!projectId || !shouldUpdateProject) return handleChangeProjectImage(url)

      uploadingImageRef.current = url
      abortController = new AbortController()

      callPagApi(
        '',
        getTokenSilently,
        updateProject,
        {
          tenantId: activeTenant?.id,
          id: projectId,
          image: url,
        },
        abortController.signal
      )
    } catch (err) {
      uploadingImageRef.current = null
      if (abortController) abortController.abort()
    }
  }

  const uploadFile = async (selectedFile: File) => {
    const extension = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')) || '.jpg'
    const uploadKey = projectId
      ? `thumbs/project-${projectId}/cover${extension}`
      : `thumbs/temp-${Date.now()}/cover${extension}`

    const controller = new AbortController()
    uploadControllerRef.current = controller

    setProgress(0)
    setStatus('uploading')
    setErrorMsg('')
    uploadingRef.current = true

    try {
      const result = await uploadProjectThumbnail(selectedFile, uploadKey, {
        getToken: async () => {
          const claims = await getIdTokenClaims()
          if (!claims?.__raw) {
            throw new Error('Unable to acquire Auth0 ID token for thumbnail upload')
          }
          return claims.__raw
        },
        signal: controller.signal,
        onProgress: (percentage) => {
          if (!mountedRef.current) return
          setProgress(percentage)
        },
      })

      if (!mountedRef.current) return
      setStatus('success')
      setProgress(100)
      uploadingRef.current = false

      handleChangeFile(result.cloudFrontUrl)
    } catch (uploadError) {
      if (!mountedRef.current) return
      setStatus('error')
      uploadingRef.current = false
      setErrorMsg(uploadError instanceof Error ? uploadError.message : 'Unable to upload image')
    } finally {
      uploadControllerRef.current = null
    }
  }

  const handleFileSelected = async (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files
    if (selectedFiles?.length) {
      await uploadFile(selectedFiles[0])
    }
  }

  const handleDrop = async (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragging(false)

    const droppedFiles = event.dataTransfer.files
    if (droppedFiles?.length) {
      await uploadFile(droppedFiles[0])
    }
  }

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragging(false)
  }

  const handleDismiss = () => {
    if (uploadControllerRef.current) {
      uploadControllerRef.current.abort()
    }
    handleCancel()
  }

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        handleDismiss()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  })

  const classes = useStyles()

  if (updateProjectRes.loading || updateProjectRes.called) return null

  return (
    <div className={classes.backdrop}>
      <Paper className={classes.modalRoot} elevation={0}>
        <input
          ref={inputRef}
          className={classes.input}
          type="file"
          accept="image/*,image/jpeg"
          onChange={handleFileSelected}
        />

        <div className={classes.header}>
          <Typography className={classes.title}>Upload project thumbnail</Typography>
          <div className={classes.headerControls}>
            {status === 'uploading' ? <CircularProgress size={18} /> : null}
            <IconButton
              aria-label="close file upload"
              className={classes.closeButton}
              onClick={handleDismiss}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
        </div>

        <div className={classes.body}>
          <div
            className={[classes.dropZone, isDragging ? classes.dropZoneActive : '']
              .filter(Boolean)
              .join(' ')}
            role="button"
            tabIndex={0}
            onClick={() => {
              inputRef.current?.click()
            }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                inputRef.current?.click()
              }
            }}
          >
            <div className={classes.iconBubble}>+</div>
            <Typography variant="body1">
              {status === 'success' ? 'Thumbnail uploaded successfully' : 'Drop project image here'}
            </Typography>
            <Typography variant="body2">
              Drag and drop, or click to choose from your computer.
            </Typography>
          </div>

          {status === 'uploading' ? (
            <div className={classes.progressWrap}>
              <LinearProgress variant="determinate" value={progress} />
              <Typography variant="caption">
                {Math.round(progress)}% uploaded
              </Typography>
            </div>
          ) : null}

          {status === 'error' ? (
            <Typography variant="caption" className={classes.error}>
              {errorMsg}
            </Typography>
          ) : null}

          <div className={classes.actions}>
            <Button size="small" color="primary" onClick={handleDismiss}>
              Cancel
            </Button>
          </div>
        </div>
      </Paper>
    </div>
  )
}


const ProjectImage = (props: any) => {
  const { image, project, shouldUpdateProject = true, handleSubmit } = props
  const classes = useStyles()
  const [editingStatus, setEditingStatus] = useState(false)

  const handleChangeProjectImage = (url: string) => {
    setEditingStatus(false)
    handleSubmit(url)
  }

  const projectImage =
    image ||
    migratedImage(project, 'image') ||
    migratedImage(project?.client, 'defaultProjectImage') ||
    migratedImage(project?.client?.brand, 'defaultProjectImage')

  return (
    <Paper className={clsx(classes.root)} elevation={0}>
      <div onClick={() => setEditingStatus(true)}>
        {projectImage ? (
          <img className={classes.projectImage} src={projectImage} alt="project thumbnail" />
        ) : (
          <div className={clsx(classes.projectImage, 'pag-flex')}>
            <Icon color="primary" style={{ fontSize: 32 }}>
              add
            </Icon>
          </div>
        )}
        <div className={clsx('image-over-layer')}></div>
      </div>
      {editingStatus && (
        <ProjectImageEdit
          projectId={project?.id}
          shouldUpdateProject={shouldUpdateProject}
          handleChangeProjectImage={handleChangeProjectImage}
          handleCancel={() => {
            setEditingStatus(false)
          }}
        />
      )}
    </Paper>
  )
}

ProjectImage.propTypes = {
  image: PropTypes.string,
  project: PropTypes.object,
  shouldUpdateProject: PropTypes.bool,
  handleCancel: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
}

ProjectImage.defaultProps = {
  handleCancel: (e: any) => {},
  handleSubmit: (e: any) => {},
}

export default ProjectImage
